/**
 * Created by Mike Naughton on 2/15/18.
 *
 * Singleton module for managing context menus
 */
import clipboard from "core/Clipboard";
import contextMenu from "context.js/context";
import BoundOperation from "core/models/BoundOperation";
import _ from "underscore";

contextMenu.init();

/**
 * Given a view, retrieves the string selector which may be used by the context menu library to identify the view.
 * @private
 * @param  {Backbone.View|jQuery|String} view Either a Backbone.View, a jquery element, or a string selector indicating the element to which the menu shall be attached.
 * @return {string}      A string selector.
 */
function getMenuSelector(view) {
  var result = "";

  if (_.isObject(view)) {
    if (view.$el) {
      result = view.$el;
    } else {
      result = view;
    }
  } else {
    result = view;
  }

  return result;
}

/**
 * Transforms menu items from the format specified by the `attachContextMenu` documentation to the format specified in http://lab.jakiestfu.com/contextjs/.
 * @private
 * @param {Array} items The array of input items.
 * @param  {Boolean} hasParent True if there is a parent item present, aka this is a recursive call, otherwise false.
 * @return {Array}            The transformed menu.
 */
function transformMenuItems(items, hasParent) {
  var result = [];

  _.each(items, function (item, idx) {
    if (!(idx === 0 && hasParent)) {
      if (_.isString(item)) {
        result.push({
          header: item,
        });
      } else if (item === true) {
        if (result.length && item[result.length - 1] !== true) {
          //don't insert dividers adjacent to each other or as the first item in the list
          result.push({
            divider: true,
          });
        }
      } else if (_.isArray(item)) {
        result.push({
          text: item[0],
          subMenu: transformMenuItems(item, true),
        });
      } else if (_.isObject(item)) {
        if (item instanceof BoundOperation) {
          if (item.available()) {
            result.push({
              text:
                (item.get("icon")
                  ? '<i class="glyphicon glyphicon-' +
                    item.get("icon") +
                    '" aria-hidden="true"></i>'
                  : "") + item.get("text"),
              action: function () {
                item.action();
              },
            });
          }
        } else {
          result.push(item);
        }
      }
    }
  });

  return result;
}

var contextMenuUpdateListenerById = {};

var ContextMenuService = {
  /**
   * Attaches a context menu to the given view.
   *
   * ## Menus
   * Elements of `menus` are interpreted in the following way:
   * - Strings will be transformed into headers.
   * - `true` values will be transformed into separators.
   * - Array values will be transformed into submenus, using the first element as the label for the menu.
   * - Objects will be transformed into menu items.
   * 		- Raw objects will be interpreted as specified on http://lab.jakiestfu.com/contextjs/
   * 		- `BoundOperation` objects will serve as the source of menu details.
   * A string will be transformed into a header, a `true` will be transformed into a separator, an array will be transformed into a submen
   * @param  {mixed} view Either a Backbone.View, a jquery element, or a string selector indicating the element to which the menu shall be attached.
   * @param  {Array} menus Descriptions of the menus to attach to the view.
   * @param  {el} a jquery element
   */
  attachContextMenu: function (view, menus, el) {
    var selector = getMenuSelector(view);
    var menuId;

    if (el) {
      menuId = contextMenu.attachDelegate(
        el,
        selector,
        transformMenuItems(menus)
      );
    } else {
      menuId = contextMenu.attachDelegate(
        "body",
        selector,
        transformMenuItems(menus)
      );
    }

    contextMenuUpdateListenerById[menuId] = function () {
      contextMenu.updateMenu(menuId, transformMenuItems(menus));
    };

    clipboard.on("update", contextMenuUpdateListenerById[menuId]);
  },

  /**
   * Detaches the context menu from the given view.
   * @param  {mixed} view Either a Backbone.View, a jquery element, or a string selector indicating the element to which the menu shall be attached.
   */
  detachContextMenu: function (view) {
    var menuId = contextMenu.destroy(getMenuSelector(view));

    if (contextMenuUpdateListenerById[menuId]) {
      clipboard.off("update", contextMenuUpdateListenerById[menuId]);
    }
  },

  /*
   * Detaches the context menu from the given view.
   * @param  {mixed} el a jquery element
   */
  detachContextMenuDelegate: function (el, selector) {
    var menuId = contextMenu.destroyDelegate(el, selector);

    if (contextMenuUpdateListenerById[menuId]) {
      clipboard.off("update", contextMenuUpdateListenerById[menuId]);
    }
  },

  /**
   * Displays a context menu at the given coordinates of the screen
   * @param  {number} x X-Coordinate on page.
   * @param  {number} y Y-Coordinate on page.
   * @param  {Array} menus Descriptions of the menus to attach to the view.
   */
  displayContextMenu: function (x, y, menus) {
    var mockEvent = $.Event("contextmenu", {
      pageX: x,
      pageY: y,
    });
    var transformedMenuItems = transformMenuItems(menus);

    if (!transformedMenuItems.id) {
      transformedMenuItems.id = _.uniqueId();
    }

    contextMenu.show(mockEvent, transformedMenuItems);
  },
};

export default ContextMenuService;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/context-menu-service.js
// module id = 171
// module chunks = 1